import React, {useContext, useState, useEffect} from "react";
import "./Header.scss";
import StyleContext from "../../contexts/StyleContext";

const SunIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const NAV_LINKS = [
  {label: "Experience", id: "experience"},
  {label: "Work", id: "engineering"},
  {label: "Projects", id: "projects"},
  {label: "Publication", id: "publication"},
  {label: "Capabilities", id: "skills"},
  {label: "Certifications", id: "achievements"},
  {label: "Contact", id: "contact"}
];

function Header() {
  const {isDark, changeTheme} = useContext(StyleContext);
  const [isOpen, setIsOpen] = useState(false);
  const [elevated, setElevated] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setElevated(window.scrollY > 10);
      if (window.scrollY > 60) setIsOpen(false);
    };
    window.addEventListener("scroll", onScroll, {passive: true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id, e) => {
    if (e) e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({behavior: "smooth"});
    setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating pill wrapper ── */}
      <div className={`nav-frame${elevated ? " nav-frame--elevated" : ""}`}>
        <header
          className={`pill-nav${isDark ? " pill-nav--dark" : ""}`}
          role="banner"
        >
          {/* Brand */}
          <a href="/" className="pill-nav__brand" aria-label="Home">
            <span className="pill-nav__mono" aria-hidden="true">
              S
            </span>
            <span className="pill-nav__name">Srimonchaari</span>
          </a>

          {/* Desktop nav */}
          <nav className="pill-nav__links" aria-label="Primary navigation">
            {NAV_LINKS.map(({label, id}) => (
              <a
                key={id}
                href={`#${id}`}
                className="pill-nav__link"
                onClick={e => scrollTo(id, e)}
              >
                {label}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="pill-nav__actions">
            <button
              className="pill-nav__theme"
              onClick={changeTheme}
              aria-label={
                isDark ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>

            <a
              href="#contact"
              className="pill-nav__cta"
              onClick={e => scrollTo("contact", e)}
            >
              Get in Touch
            </a>

            <button
              className={`pill-nav__burger${isOpen ? " is-open" : ""}`}
              onClick={() => setIsOpen(v => !v)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              <span />
              <span />
            </button>
          </div>
        </header>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        className={`mobile-nav${isDark ? " mobile-nav--dark" : ""}${
          isOpen ? " mobile-nav--open" : ""
        }`}
        aria-hidden={!isOpen}
        role="dialog"
        aria-label="Navigation menu"
      >
        {NAV_LINKS.map(({label, id}) => (
          <a
            key={id}
            href={`#${id}`}
            className="mobile-nav__link"
            onClick={e => scrollTo(id, e)}
            tabIndex={isOpen ? 0 : -1}
          >
            {label}
          </a>
        ))}
        <div className="mobile-nav__rule" />
        <a
          href="#contact"
          className="mobile-nav__cta"
          onClick={e => scrollTo("contact", e)}
          tabIndex={isOpen ? 0 : -1}
        >
          Get in Touch →
        </a>
      </div>
    </>
  );
}

export default Header;
